import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { activeTab, CAPTURE_SLOT, TABS, type NavItem } from '../nav.js';
import { ToastProvider, useToast } from '../lifeos/toast.js';
import { CaptureProvider, useCapture } from '../capture/CaptureProvider.js';
import { logout, me } from '../api.js';
import Icon from '../ui/Icon.js';
import AlertsBell from './AlertsBell.js';
import SearchPalette from './SearchPalette.js';
import { isMac, SidebarNav } from './Sidebar.js';

/**
 * App shell (spec 2026-10-14-workspaces): a 264px sidebar on desktop (Search,
 * Capture, main destinations, one row per workspace, the folded rest), an iOS
 * tab bar on the phone with the raised Capture button in the middle plus a
 * menu sheet holding the full sidebar. ⌘K / Ctrl K / "/" open search. Confirms the session on mount (a lapsed
 * cookie triggers the api wrapper's redirect to /login). The toast provider
 * wraps the whole shell, so an Undo window survives navigating between pages
 * and sign-out can wait for it; the capture provider sits inside it so a
 * capture's Undo can hand off to a toast.
 */
export default function Layout(): JSX.Element {
  return (
    <ToastProvider>
      <CaptureProvider>
        <Shell />
      </CaptureProvider>
    </ToastProvider>
  );
}

/** True when a keypress belongs to a text field rather than the app. */
function typingIn(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

function Shell(): JSX.Element {
  const toast = useToast();
  const capture = useCapture();
  const [email, setEmail] = useState<string>('');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const active = activeTab(pathname);
  const [searching, setSearching] = useState(false);
  const [menu, setMenu] = useState(false);
  const mac = isMac();

  // Navigating anywhere closes the phone menu.
  useEffect(() => setMenu(false), [pathname]);

  useEffect(() => {
    let alive = true;
    me()
      .then((r) => {
        if (alive) setEmail(r.user.email);
      })
      .catch(() => {
        /* 401 already redirected by the api wrapper */
      });
    return () => {
      alive = false;
    };
  }, []);

  // ⌘K / Ctrl K anywhere toggles search; "/" (outside a field or dialog) opens
  // it; "c" (likewise) opens capture, typed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearching((v) => !v);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (e.key !== 'c' && e.key !== '/') return;
      if (typingIn(e.target) || document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      if (e.key === '/') setSearching(true);
      else capture.open('type');
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [capture]);

  async function onLogout(): Promise<void> {
    try {
      // A change still in its undo window is sent while the session is alive;
      // after logout it would only meet a 401.
      await toast.flush();
      await logout();
    } finally {
      navigate('/login');
    }
  }

  const tab = (item: NavItem): JSX.Element => {
    const on = active === item.to;
    return (
      <Link
        key={item.to}
        to={item.to}
        aria-current={on ? 'page' : undefined}
        className={`tabbar__item${on ? ' tabbar__item--active' : ''}`}
      >
        <Icon name={item.icon} size={25} filled={on} />
        <span className="tabbar__label">{item.short}</span>
      </Link>
    );
  };

  const searchBtn = (
    <button
      type="button"
      className="sb-search"
      onClick={() => {
        setMenu(false);
        setSearching(true);
      }} aria-keyshortcuts="Meta+K Control+K /">
      <Icon name="search" size={18} />
      <span>Search</span>
      <span className="kbd" aria-hidden="true">
        {mac ? '⌘K' : 'Ctrl K'}
      </span>
    </button>
  );

  const foot = (
    <div className="rail__foot">
      {email && (
        <div className="rail__user" title={email}>
          {email}
        </div>
      )}
      <button className="linkbtn" onClick={onLogout}>
        Sign out
      </button>
    </div>
  );

  return (
    <div className="app">
      <aside className="rail">
        <div className="brand">
          <span className="brand__mark" aria-hidden="true">
            <Icon name="activity" size={17} />
          </span>
          <span className="brand__name">Vitals</span>
          <AlertsBell />
        </div>
        {searchBtn}
        <button type="button" className="rail__capture" onClick={() => capture.open()} aria-keyshortcuts="c">
          <Icon name="mic" size={18} filled />
          <span>Capture</span>
          <span className="kbd" aria-hidden="true">
            C
          </span>
        </button>
        <div className="rail__scroll">
          <SidebarNav />
        </div>
        {foot}
      </aside>

      <header className="topbar">
        <button type="button" className="topbar__btn" aria-label="Menu" aria-expanded={menu} onClick={() => setMenu(true)}>
          <Icon name="list" size={22} />
        </button>
        <span className="topbar__name">Vitals</span>
        <AlertsBell />
        <button type="button" className="topbar__btn" aria-label="Search" onClick={() => setSearching(true)}>
          <Icon name="search" size={22} />
        </button>
      </header>

      <main className="main">
        <Outlet />
      </main>

      <nav className="tabbar" aria-label="Tabs">
        {TABS.slice(0, CAPTURE_SLOT).map(tab)}
        <div className="tabbar__slot">
          <CaptureButton />
        </div>
        {TABS.slice(CAPTURE_SLOT).map(tab)}
        <button type="button" className="tabbar__item" aria-label="Search" onClick={() => setSearching(true)}>
          <Icon name="search" size={25} />
          <span className="tabbar__label">Search</span>
        </button>
      </nav>

      {menu && (
        <div className="sb-sheet" role="dialog" aria-modal="true" aria-label="Menu" onKeyDown={(e) => e.key === 'Escape' && setMenu(false)}>
          <button type="button" className="sb-sheet__backdrop" aria-label="Close menu" tabIndex={-1} onClick={() => setMenu(false)} />
          <div className="sb-sheet__panel">
            <div className="sb-sheet__head">
              <span className="brand__name">Vitals</span>
              <button type="button" className="topbar__btn" aria-label="Close menu" onClick={() => setMenu(false)} autoFocus>
                <Icon name="x" size={22} />
              </button>
            </div>
            {searchBtn}
            <SidebarNav />
            {foot}
          </div>
        </div>
      )}

      {searching && <SearchPalette onClose={() => setSearching(false)} />}
    </div>
  );
}

/** Tap = capture (voice when the browser can), long-press = type. */
function CaptureButton(): JSX.Element {
  const capture = useCapture();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const long = useRef(false);

  const clear = (): void => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => clear, []);

  return (
    <button
      type="button"
      className="tabbar__capture"
      aria-label="Capture"
      onPointerDown={() => {
        long.current = false;
        clear();
        timer.current = setTimeout(() => {
          long.current = true;
          try {
            navigator.vibrate?.(10);
          } catch {
            /* unsupported */
          }
          capture.open('type');
        }, 450);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onContextMenu={(e) => e.preventDefault()}
      onClick={() => {
        clear();
        if (long.current) {
          long.current = false;
          return;
        }
        capture.open();
      }}
    >
      <Icon name="mic" size={28} filled />
    </button>
  );
}
